// Rendu des toasts (Toast + ToastContainer) uniquement. L'état et le hook `useToast` vivent dans
// ToastContext.tsx — <ToastProvider> est monté dans app/layout.tsx (#64 : un second useToast exporté
// ici, sans provider, donnait un hook qui ne faisait rien).
'use client';
import { useEffect, useRef } from 'react';
import LegacyStyle from '../LegacyStyle';

type ToastType = 'success' | 'error' | 'info' | 'warning';

// Une erreur reste affichée plus longtemps qu'un succès : au comptoir, le staff regarde souvent le
// client, pas l'écran, au moment où elle apparaît (#63). Toujours fermable avant avec ✕.
const DURATION_MS: Record<ToastType, number> = { success: 4000, info: 4000, warning: 4000, error: 8000 };

interface ToastProps {
  message: string;
  type: ToastType;
  duration?: number;
  onClose: () => void;
}

export function Toast({ message, type, duration = DURATION_MS[type], onClose }: ToastProps) {
  // onClose change à chaque rendu du conteneur (ajout ou retrait d'un autre toast) : dans les
  // dépendances, il relançait le minuteur et prolongeait les toasts déjà affichés.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const timer = setTimeout(() => onCloseRef.current(), duration);
    return () => clearTimeout(timer);
  }, [duration]);

  const icons = {
    success: '✅',
    error: '❌',
    info: 'ℹ️',
    warning: '⚠️',
  };

  const colors = {
    // Tokens du design system (#596) : fond plein et texte contrasté, en clair comme en sombre.
    success: { bg: 'var(--ok)', border: 'var(--ok)', fg: 'var(--ok-fg)' },
    error: { bg: 'var(--bad)', border: 'var(--bad)', fg: 'var(--bad-fg)' },
    info: { bg: 'var(--info)', border: 'var(--info)', fg: 'var(--info-fg)' },
    warning: { bg: 'var(--warn)', border: 'var(--warn)', fg: 'var(--warn-fg)' },
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        pointerEvents: 'auto',
        padding: '1rem 1.5rem',
        background: colors[type].bg,
        border: `2px solid ${colors[type].border}`,
        borderRadius: '12px',
        color: colors[type].fg,
        fontWeight: 600,
        fontSize: '0.95rem',
        animation: 'slideIn 0.3s ease-out',
        boxShadow: 'var(--shadow-lg)',
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        maxWidth: '400px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <span style={{ fontSize: '1.25rem' }}>{icons[type]}</span>
      {/* color explicite : la règle historique `span { color: var(--text) }` l'emporterait sur l'héritage. */}
      <span style={{ flex: 1, color: colors[type].fg }}>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: 'color-mix(in srgb, currentColor 18%, transparent)',
          border: 'none',
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          color: colors[type].fg,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.875rem',
        }}
        aria-label="Fermer la notification"
      >
        ✕
      </button>
    </div>
  );
}

// Toast Container component
export function ToastContainer({ toasts, removeToast }: { toasts: any[]; removeToast: (id: string) => void }) {
  // Empilés plutôt que superposés au même endroit : une erreur (8 s) ne se cache pas sous un succès
  // plus récent pour réapparaître à sa disparition.
  return (
    <>
      <div style={{ position: 'fixed', bottom: '2rem', right: '2rem', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem', pointerEvents: 'none' }}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
      <LegacyStyle css={`
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `} />
    </>
  );
}
